import { sessionFetch } from '../../utils/sessionFetch';
import { useEffect, useState, useCallback, useRef } from "react";
import EditUser from './EditUser'
import CreateUserTab from './CreateUserTab'
import './GestioApp.css'
import { NoSignal } from "../Interface";
import { fetchAPI, postAPI, getSubdomain, getServerEndpoint } from '../../utils/utils';
import UserInfo from "../login/UserInfo";
import Pressable from "../other/Pressable";
import HeaderTabs from "../interface/HeaderTabs";
import { can } from '../../utils/permissions';
import ExcelImportTab from './ExcelImportTab';
import RolePermissionsEditor from './RolePermissionsEditor';
import FamiliesTab from './FamiliesTab';
import DeletedCastellersTab from './DeletedCastellersTab';
import CastellersTable from './table/CastellersTable';
import './table/CastellersTable.css';
import { hasFamiliesFeature } from '../../utils/colla-features';
import HistoryPanel from './HistoryPanel';
import { hasValue } from './userFieldUtils';
import { requestDeletedUserRestore } from './restoreDeletedUser';

const COLLA = getSubdomain();
const serverEndpoint = getServerEndpoint(COLLA);

function GestioApp(props) {
    const { socket, userId } = props;
    const [users, setUsers] = useState([]);
    const [editResetVersion, setEditResetVersion] = useState(0);
    const [deletedUsers, setDeletedUsers] = useState([]);

    const [selectedUser, setSelectedUser] = useState({});
    const [editClosed, setEditClosed] = useState(true);

    const [filter, setFilter] = useState('');
    const [filterInfo, setFilterInfo] = useState(false);
    const [filterHidden, setFilterHidden] = useState(false);
    const [filterCanalla, setFilterCanalla] = useState(false);
    const [filterMusic, setFilterMusic] = useState(false);
    const [filterNovell, setFilterNovell] = useState(false);
    const [filterLesionat, setFilterLesionat] = useState(false);
    const [filterSoci, setFilterSoci] = useState(false);
    const [filterExtern, setFilterExtern] = useState(false);
    const [filterNoNotifications, setFilterNoNotifications] = useState(false);

    // Etiquetes state
    const [showEtiquetes, setShowEtiquetes] = useState(false);
    const [allEtiquetes, setAllEtiquetes] = useState([]);
    const [userEtiquetes, setUserEtiquetes] = useState({}); // { userId: [etiquetaId1, ...], ... }

    // Undo/History state
    const [isUndoing, setIsUndoing] = useState(false);
    const [undoToast, setUndoToast] = useState(null);
    const [casteHistory, setCasteHistory] = useState([]);

    const [sortBy, setSortBy] = useState('mote');
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortPreset, setSortPreset] = useState('');

    const handleSort = (field) => {
        // Reset preset when clicking column headers
        setSortPreset('');

        if (sortBy === field) {
            // Mateix camp: toggle direcció
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // Nou camp: checkboxes i rols descendent (valors alts primer), altres ascendent
            const descFields = ['canalla', 'music', 'lesionat', 'novell', 'soci', 'casteller', 'es_tecnica', 'es_junta'];
            const isDescDefault = descFields.includes(field) || field.startsWith('etiqueta_');
            setSortBy(field);
            setSortDirection(isDescDefault ? 'desc' : 'asc');
        }
    };

    const [activeTab, setActiveTab] = useState('usuaris');
    // The Paperera (deleted castellers) and the changes' history are for who manages
    // users, like the server's check
    const canManageUsers = can(props.userInfo, 'MANAGE_USER');

    // Hide families tab for university colles
    const gestioTabs = hasFamiliesFeature()
        ? ['usuaris', 'crear', 'families', 'exportar', 'importar', 'paperera', 'permisos']
        : ['usuaris', 'crear', 'exportar', 'importar', 'paperera', 'permisos'];

    const getTabFromPath = () => {
        const path = window.location.pathname;
        const parts = path.split('/');
        const lastPart = parts[parts.length - 1];

        // Redirect to usuaris if families tab is not available
        if (lastPart === 'families' && !hasFamiliesFeature()) {
            return 'usuaris';
        }

        if (gestioTabs.includes(lastPart)) {
            return lastPart;
        }

        return 'usuaris';
    };

    const changeTab = (tab) => {
        setActiveTab(tab);

        const currentPath = window.location.pathname;
        const tabPattern = hasFamiliesFeature()
            ? /\/(usuaris|crear|families|exportar|importar|paperera|permisos)$/
            : /\/(usuaris|crear|exportar|importar|paperera|permisos)$/;
        const basePath = currentPath.replace(tabPattern, '');
        const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;

        window.history.pushState({ tab }, '', `${normalizedBasePath}${tab}`);
    };

    const normalize = (str) => {
        return String(str)
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")    
            .toLowerCase();
    };

    const isUserWithMissingInfo = (user) =>
        [...Object.values((
            ({ nom, 'primer-cognom': primer, mote, altura, es_tecnica }) =>
            ({ nom, 'primer-cognom': primer, mote, altura, es_tecnica })
        )
        (user))]
            .filter(val => val === null || val === '-')
            .length > 0;

    const show_popup = user => {
        setEditClosed(false);
        setSelectedUser(user);
    };

    const normalizeUserChangesForState = (changes = {}) => {
        const normalized = { ...changes };

        // UI envia "tecnica/junta" però els registres d'usuari fan servir "es_tecnica/es_junta".
        if (Object.prototype.hasOwnProperty.call(changes, 'tecnica')) {
            normalized.es_tecnica = parseInt(changes.tecnica);
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'junta')) {
            normalized.es_junta = parseInt(changes.junta);
        }

        return normalized;
    };

    const updateUser = (user, changes={}) => {
        const responses = {
            'id': user.id,
            'nom': user.nom,
            'primer-cognom': user['primer-cognom'],
            'segon-cognom': user['segon-cognom'],
            'mote': user.mote,
            'altura': parseInt(user.altura),
            'altura_mans': parseInt(user.altura_mans),
            'tecnica': parseInt(user['es_tecnica']),
            'junta': parseInt(user['es_junta']),
            'hidden': user.hidden ? 1 : 0,
            'canalla': user.canalla ? 1 : 0,
            'music': user.music ? 1 : 0,
            'lesionat': user.lesionat ? 1 : 0,
            'novell': user.novell ? 1 : 0,
            'extern': user.extern ? 1 : 0,
            'casteller': user.casteller ? 1 : 0,
            '_actorId': userId, // Fallback for socket.userId
        };

        const new_responses = {
            ...responses,
            ...changes
        }

        const normalizedChanges = normalizeUserChangesForState(changes);

        // Actualització optimista de l'estat local
        setUsers(prevUsers => prevUsers.map(u =>
            u.id === user.id ? { ...u, ...normalizedChanges } : u
        ));

        socket.emit('.edit_user', new_responses);
    };

    const deleteUser = (user) => {
        // Actualització optimista de l'estat local
        setUsers(prevUsers => prevUsers.filter(u => u.id !== user.id));
        setDeletedUsers(prevUsers => [{ ...user, deleted: 1, deleted_at: new Date().toISOString() }, ...prevUsers]);

        socket.emit('.delete_user', {
            ...user,
            _deletionConfirmed: true,
        });
    };

    const restoreDeletedUser = useCallback(user => requestDeletedUserRestore({
        socket,
        user,
        actorId: userId,
    }), [socket, userId]);

    // Undo last change (optimistic update)
    const handleUndo = useCallback(() => {
        if (isUndoing || casteHistory.length === 0) return;
        
        const lastEntry = casteHistory[0];
        const { action, casteller_id, snapshot } = lastEntry;
        
        // Parse snapshot
        let snapshotData = null;
        if (snapshot) {
            try {
                snapshotData = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
            } catch (e) {
                console.error('Error parsing snapshot:', e);
            }
        }
        const previousState = snapshotData?.previousState || snapshotData;
        
        // Save current state for rollback
        const previousUsers = [...users];
        const previousHistory = [...casteHistory];
        
        // Apply optimistic update immediately
        if (action === 'create') {
            // Undo creation: remove the user
            setUsers(prev => prev.filter(u => u.id !== casteller_id));
        } else if (action === 'delete' && previousState) {
            // Undo deletion: restore the user
            setUsers(prev => [...prev, previousState]);
        } else if (action === 'edit' && previousState) {
            // Undo edit: restore previous state
            setUsers(prev => prev.map(u => u.id === casteller_id ? { ...u, ...previousState } : u));
        }
        
        // Remove from local history immediately
        setCasteHistory(prev => prev.slice(1));
        
        // Show optimistic success toast
        const actionLabel = {
            'create': 'Creació desfeta',
            'edit': 'Edició desfeta', 
            'delete': 'Eliminació desfeta'
        }[action] || 'Canvi desfet';
        setUndoToast({ type: 'success', message: actionLabel });
        setTimeout(() => setUndoToast(null), 3000);
        
        setIsUndoing(true);
        
        // Send to server
        socket.emit('.undo_casteller_change', (response) => {
            setIsUndoing(false);
            
            if (!response?.success) {
                // Rollback on error
                setUsers(previousUsers);
                setCasteHistory(previousHistory);
                setUndoToast({ 
                    type: 'error', 
                    message: response?.error || 'Error al desfer el canvi' 
                });
                setTimeout(() => setUndoToast(null), 3000);
            }
            // No need to refresh - local state is already correct
        });
    }, [socket, isUndoing, casteHistory, users]);

    // Keep a ref to handleUndo to avoid re-running effects when it changes
    const handleUndoRef = useRef(handleUndo);
    useEffect(() => {
        handleUndoRef.current = handleUndo;
    }, [handleUndo]);

    // Reload after reconnecting, including the login's credential refresh.
    useEffect(() => {
        const requestDeletedUsers = () => {
            if (canManageUsers) socket.emit('.request_deleted_users');
        };
        const requestData = () => {
            socket.emit('.request_users');
            requestDeletedUsers();
            if (canManageUsers) socket.emit('.request_casteller_history', { limit: 50 });
        };

        const handleUsers = (info) => setUsers(info);
        const handleDeletedUsers = (info) => setDeletedUsers(info || []);
        const handleCastellerHistory = (history) => setCasteHistory(history || []);
        const handleUserChange = () => {
            socket.emit('.request_users');
            requestDeletedUsers();
        };
        const handleEditUserError = (payload) => {
            const errMsg = payload?.error || 'No s\'ha pogut editar l\'usuari';
            window.alert(errMsg);
            // Discard rejected drafts and pending debounces in the table.
            setEditResetVersion(version => version + 1);
            socket.emit('.request_users');
            requestDeletedUsers();
        };

        const handleOperationError = (payload) => {
            if (payload?.event !== '.edit_user') return;
            handleEditUserError({ error: payload.error === 'FORBIDDEN'
                ? 'No tens permís per editar aquest casteller. El canvi no s’ha desat.'
                : 'No s’ha pogut desar el canvi. Torna-ho a provar.' });
        };

        socket.on('.operation_error', handleOperationError);
        socket.on('.users', handleUsers);
        socket.on('.deleted_users', handleDeletedUsers);
        socket.on('.casteller_history', handleCastellerHistory);
        socket.on('.user_change', handleUserChange);
        socket.on('.edit_user_error', handleEditUserError);
        socket.on('connect', requestData);

        if (socket.connected) requestData();

        return () => {
            socket.off('.operation_error', handleOperationError);
            socket.off('connect', requestData);
            socket.off('.users', handleUsers);
            socket.off('.deleted_users', handleDeletedUsers);
            socket.off('.casteller_history', handleCastellerHistory);
            socket.off('.user_change', handleUserChange);
            socket.off('.edit_user_error', handleEditUserError);
        };
    }, [socket, canManageUsers]);

    // Fetch etiquetes data
    useEffect(() => {
        if (can(props.userInfo, 'ACCESS_ETIQUETES')) {
            fetchAPI('/etiquetes', setAllEtiquetes);
            fetchAPI('/all_user_etiquetes', setUserEtiquetes);
        }
    }, [props.userInfo]);

    // Page setup and keyboard shortcuts
    useEffect(() => {
        document.title = `Editar castellers - Aleta`;

        const tabFromPath = getTabFromPath();
        setActiveTab(tabFromPath);

        const handlePopState = () => {
            setActiveTab(getTabFromPath());
        };

        // Keyboard shortcut for undo (Ctrl+Z / Cmd+Z)
        const handleKeyDown = (e) => {
            // Check if Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                // Don't trigger if user is typing in an input/textarea
                const activeElement = document.activeElement;
                const isInputActive = activeElement?.tagName === 'INPUT' || 
                                     activeElement?.tagName === 'TEXTAREA' ||
                                     activeElement?.isContentEditable;
                
                if (!isInputActive) {
                    e.preventDefault();
                    handleUndoRef.current();
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const downloadDatabaseAsExcel = () => {
        sessionFetch(`${serverEndpoint}/api/export_castellers_as_excel`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(res => res.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const date = new Date();
                const formattedDate = `${date.getDate()}-${date.getMonth()+1}-${date.getFullYear()}`;
                a.download = `${COLLA}-${formattedDate}.xlsx`;
                a.click();
            })
            .catch(e => console.log(e));      
    };

    const sortCastellers = (a, b) => {
        const direction = sortDirection === 'asc' ? 1 : -1;

        if (sortBy === 'mote') {
            if (!a.mote && !b.mote) return 0;
            if (!a.mote) return 1;
            if (!b.mote) return -1;
            return direction * a.mote.localeCompare(b.mote);
        }

        if (sortBy === 'nom') {
            const nomA = `${a.nom || ''} ${a['primer-cognom'] || ''}`.trim();
            const nomB = `${b.nom || ''} ${b['primer-cognom'] || ''}`.trim();
            if (!nomA && !nomB) return 0;
            if (!nomA) return 1;
            if (!nomB) return -1;
            return direction * nomA.localeCompare(nomB);
        }

        if (sortBy === 'primer-cognom') {
            const cognomA = a['primer-cognom'] || '';
            const cognomB = b['primer-cognom'] || '';
            if (!cognomA && !cognomB) return 0;
            if (!cognomA) return 1;
            if (!cognomB) return -1;
            return direction * cognomA.localeCompare(cognomB);
        }

        if (sortBy === 'segon-cognom') {
            const cognomA = a['segon-cognom'] || '';
            const cognomB = b['segon-cognom'] || '';
            if (!cognomA && !cognomB) return 0;
            if (!cognomA) return 1;
            if (!cognomB) return -1;
            return direction * cognomA.localeCompare(cognomB);
        }

        if (sortBy === 'altura') {
            const alturaA = a.altura || 0;
            const alturaB = b.altura || 0;
            return direction * (alturaA - alturaB);
        }

        if (sortBy === 'altura_mans') {
            const alturaMansA = a.altura_mans || 0;
            const alturaMansB = b.altura_mans || 0;
            return direction * (alturaMansA - alturaMansB);
        }

        if (sortBy === 'es_tecnica') {
            return direction * ((a.es_tecnica || 0) - (b.es_tecnica || 0));
        }

        if (sortBy === 'es_junta') {
            return direction * ((a.es_junta || 0) - (b.es_junta || 0));
        }

        if (sortBy === 'canalla') {
            return direction * ((a.canalla || 0) - (b.canalla || 0));
        }

        if (sortBy === 'music') {
            return direction * ((a.music || 0) - (b.music || 0));
        }

        if (sortBy === 'lesionat') {
            return direction * ((a.lesionat || 0) - (b.lesionat || 0));
        }

        if (sortBy === 'novell') {
            return direction * ((a.novell || 0) - (b.novell || 0));
        }

        if (sortBy === 'soci') {
            return direction * ((a.soci || 0) - (b.soci || 0));
        }

        if (sortBy === 'casteller') {
            return direction * ((a.casteller || 0) - (b.casteller || 0));
        }

        if (sortBy === 'id') {
            return direction * (a.id - b.id);
        }

        if (sortBy === 'missing_info') {
            const aMissing = isUserWithMissingInfo(a);
            const bMissing = isUserWithMissingInfo(b);
            if (aMissing && !bMissing) return -1;
            if (!aMissing && bMissing) return 1;
            return 0;
        }

        // Etiqueta sorting (sortBy format: "etiqueta_123")
        if (sortBy.startsWith('etiqueta_')) {
            const etiquetaId = parseInt(sortBy.replace('etiqueta_', ''));
            const aHas = userEtiquetes[a.id]?.includes(etiquetaId) ? 1 : 0;
            const bHas = userEtiquetes[b.id]?.includes(etiquetaId) ? 1 : 0;
            return direction * (aHas - bHas);
        }

        return 0;
    };

    const hasAnyFilter = filterInfo || filterHidden || 
        filterCanalla || filterMusic || filterNovell || filterLesionat || 
        filterSoci || filterExtern || filterNoNotifications;

    const filteredUsers = users
        // Text search filter
        .filter(user => `${normalize(user.nom)} ${normalize(user['primer-cognom'])} ${normalize(user['segon-cognom'])} ${normalize(user.mote)}`.includes(normalize(filter)))
        // Info filter
        .filter(user => filter !== '' ? true : filterInfo ? isUserWithMissingInfo(user) : true)
        // Hidden filter - show hidden only when checked, hide hidden when unchecked
        .filter(user => filter !== '' ? true : filterHidden ? user.hidden === 1 : user.hidden !== 1)
        // Type filters
        .filter(user => filter !== '' ? true : filterCanalla ? user.canalla === 1 : true)
        .filter(user => filter !== '' ? true : filterMusic ? user.music === 1 : true)
        .filter(user => filter !== '' ? true : filterNovell ? user.novell === 1 : true)
        .filter(user => filter !== '' ? true : filterSoci ? user.soci === 1 : true)
        .filter(user => filter !== '' ? true : filterExtern ? user.extern === 1 : true)
        // Status filters
        .filter(user => filter !== '' ? true : filterLesionat ? user.lesionat === 1 : true)
        .filter(user => filter !== '' ? true : filterNoNotifications ? user.expo_push_token === null : true)
        .sort(sortCastellers)

    // Handler for toggling etiquetes on users
    const handleEtiquetaToggle = useCallback((userId, etiquetaId, checked) => {
        // Optimistic update
        setUserEtiquetes(prev => {
            const userEtiquetaList = prev[userId] || [];
            if (checked) {
                return { ...prev, [userId]: [...userEtiquetaList, etiquetaId] };
            } else {
                return { ...prev, [userId]: userEtiquetaList.filter(id => id !== etiquetaId) };
            }
        });

        // Send to server
        if (checked) {
            postAPI('/add_etiqueta', { user: userId, etiqueta: etiquetaId }, () => {});
        } else {
            postAPI('/drop_etiqueta', { user: userId, etiqueta: etiquetaId }, () => {});
        }
    }, []);

    const resetAllFilters = () => {
        setFilter('');
        setFilterInfo(false);
        setFilterHidden(false);
        setFilterCanalla(false);
        setFilterMusic(false);
        setFilterNovell(false);
        setFilterLesionat(false);
        setFilterSoci(false);
        setFilterExtern(false);
        setFilterNoNotifications(false);
    };

    const renderUsuarisTab = () => (
        <>
            <div className="filters-container">
                {/* Search + Basic filters row */}
                <div className="filters-basic-row">
                    <input 
                        className="search-input"
                        placeholder="Busca un usuari..." 
                        type="text" 
                        value={filter} 
                        onChange={ev => setFilter(ev.target.value)} 
                    />
                    
                    <div className="basic-filters">
                        <label className={`filter-chip ${filterHidden ? 'active' : ''}`}>
                            <input type="checkbox" checked={filterHidden} onChange={() => setFilterHidden(prev => !prev)} />
                            <span>Amagats</span>
                        </label>
                        {can(props.userInfo, 'ACCESS_ETIQUETES') ? (
                            <button 
                                className={`filter-chip gestio-etiquetes-toggle ${showEtiquetes ? 'active' : ''}`}
                                onClick={() => setShowEtiquetes(prev => !prev)}
                            >
                                <i className="fas fa-tags"></i>
                                <span>Mostrar etiquetes</span>
                            </button>
                        ) : null}
                    </div>

                    <select
                        className="sort-select-compact"
                        value={sortPreset}
                        onChange={ev => {
                            const value = ev.target.value;
                            setSortPreset(value);

                            if (value === 'new_members') {
                                setSortBy('id');
                                setSortDirection('desc');
                            } else if (value === 'missing_info') {
                                setSortBy('missing_info');
                                setSortDirection('desc');
                            }
                        }}
                    >
                        <option value="">Ordenar per...</option>
                        <option value="new_members">Nous membres primer</option>
                        <option value="missing_info">Falta informació primer</option>
                    </select>
                </div>

                {/* Results count and reset */}
                <div className="filter-results-row">
                    <span className="results-count">
                        {filteredUsers.length} de {users.length} castellers
                    </span>
                    {hasAnyFilter || filter !== '' ? (
                        <button className="reset-filters-btn" onClick={resetAllFilters}>
                            Netejar filtres
                        </button>
                    ) : null}
                </div>
            </div>

            <div>
                <EditUser user={selectedUser} closed={editClosed} setClosed={setEditClosed} socket={socket} {...props} />

                <CastellersTable
                    key={editResetVersion}
                    users={filteredUsers}
                    updateUser={updateUser}
                    deleteUser={deleteUser}
                    show_popup={show_popup}
                    userInfo={props.userInfo}
                    COLLA={COLLA}
                    socket={socket}
                    userId={userId}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    showEtiquetes={showEtiquetes}
                    allEtiquetes={allEtiquetes}
                    userEtiquetes={userEtiquetes}
                    onEtiquetaToggle={handleEtiquetaToggle}
                />
            </div>
        </>
    );

    const renderExportarTab = () => {
        const previewUsers = users.slice(0, 10);
        const EXPORT_HEADERS = ['Nom', 'Primer cognom', 'Segon cognom', 'Sobrenom', 'Altura', 'Soci'];
        
        return (
            <div className="export-tab">
                <h2>Exportar base de dades</h2>
                <p>Pots descarregar la base de dades completa en format Excel ({users.length} castellers).</p>
                
                <button 
                    onClick={downloadDatabaseAsExcel}
                    style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        marginBottom: '2rem'
                    }}
                >
                    <i className="fas fa-download" style={{ marginRight: 8 }}></i>
                    Descarregar Excel
                </button>

                {users.length > 0 ? (
                    <div>
                        <h3 style={{ marginBottom: '1rem', color: '#666' }}>
                            Previsualització ({previewUsers.length} de {users.length})
                        </h3>
                        <div style={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            overflow: 'auto',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '0.9rem'
                            }}>
                                <thead>
                                    <tr>
                                        {EXPORT_HEADERS.map(h => (
                                            <th key={h} style={{
                                                backgroundColor: '#f7f7f7',
                                                padding: '0.8rem',
                                                textAlign: 'left',
                                                fontWeight: 600,
                                                position: 'sticky',
                                                top: 0,
                                                borderBottom: '1px solid #e0e0e0'
                                            }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewUsers.map((user, idx) => (
                                        <tr key={user.id} style={{
                                            backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa'
                                        }}>
                                            <td style={{ padding: '0.7rem 0.8rem', borderBottom: '1px solid #eee' }}>
                                                {user.nom || '-'}
                                            </td>
                                            <td style={{ padding: '0.7rem 0.8rem', borderBottom: '1px solid #eee' }}>
                                                {user['primer-cognom'] || '-'}
                                            </td>
                                            <td style={{ padding: '0.7rem 0.8rem', borderBottom: '1px solid #eee' }}>
                                                {user['segon-cognom'] || '-'}
                                            </td>
                                            <td style={{ padding: '0.7rem 0.8rem', borderBottom: '1px solid #eee' }}>
                                                {user.mote || '-'}
                                            </td>
                                            <td style={{ padding: '0.7rem 0.8rem', borderBottom: '1px solid #eee' }}>
                                                {hasValue(user.altura) ? user.altura : '-'}
                                            </td>
                                            <td style={{ padding: '0.7rem 0.8rem', borderBottom: '1px solid #eee' }}>
                                                {user.soci === 1 ? 'Sí' : 'No'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {users.length > 10 ? (
                            <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                                ... i {users.length - 10} castellers més
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    };

    const renderImportarTab = () => (
        <ExcelImportTab
            socket={socket}
            COLLA={COLLA}
            existingCastellers={users}
        />
    );

    const renderPapereraTab = () => (
        <DeletedCastellersTab
            deletedUsers={deletedUsers}
            onRestore={restoreDeletedUser}
        />
    );

    const renderPermisosTab = () => (
        <>
            { can(props.userInfo, 'MANAGE_ROLE_PERMISSIONS') ? (
                <RolePermissionsEditor userInfo={props.userInfo} />
            ) : (
                <div style={{ 
                    textAlign: 'center', 
                    marginTop: 50,
                    padding: 40,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 12,
                    maxWidth: 600,
                    margin: '50px auto'
                }}>
                    <h2 style={{ color: '#dc3545' }}>⛔ Accés denegat</h2>
                    <p style={{ color: '#666', fontSize: 16 }}>
                        No tens permisos per gestionar els rols de la colla.
                    </p>
                </div>
            )}
        </>
    );

    // Convert users array to castellersInfo object format for FamiliesTab
    const castellersInfo = users.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
    }, {});

    const renderFamiliesTab = () => (
        <FamiliesTab 
            socket={socket}
            castellersInfo={castellersInfo}
        />
    );

    const renderCrearTab = () => (
        <CreateUserTab
            socket={socket}
            onCreated={() => changeTab('usuaris')}
            {...props}
        />
    );

    const renderFamiliesNotAvailable = () => (
        <div style={{
            textAlign: 'center',
            marginTop: 50,
            padding: 40,
            backgroundColor: '#f8f9fa',
            borderRadius: 12,
            maxWidth: 600,
            margin: '50px auto'
        }}>
            <h2 style={{ color: '#6c757d' }}>📚 Funcionalitat no disponible</h2>
            <p style={{ color: '#666', fontSize: 16, lineHeight: 1.6 }}>
                La gestió de grups familiars no està disponible per a colles universitàries.
            </p>
            <p style={{ color: '#888', fontSize: 14, marginTop: 20 }}>
                Si necessites aquesta funcionalitat, contacta amb l'equip d'Aleta.
            </p>
            <button
                onClick={() => changeTab('usuaris')}
                style={{
                    marginTop: 20,
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14
                }}
            >
                Tornar a Usuaris
            </button>
        </div>
    );

    const renderTabContent = () => {
        if (activeTab === 'usuaris') {
            return renderUsuarisTab();
        }

        if (activeTab === 'crear') {
            return renderCrearTab();
        }

        if (activeTab === 'families') {
            return hasFamiliesFeature() ? renderFamiliesTab() : renderFamiliesNotAvailable();
        }

        if (activeTab === 'exportar') {
            return renderExportarTab();
        }

        if (activeTab === 'importar') {
            return renderImportarTab();
        }

        if (activeTab === 'paperera') {
            return canManageUsers ? renderPapereraTab() : renderUsuarisTab();
        }

        if (activeTab === 'permisos') {
            return renderPermisosTab();
        }

        return null;
    };

    return (
        <>
            <NoSignal socket={socket} />

            <div style={{ padding: 20, width: '100%', boxSizing: 'border-box' }}>
                <UserInfo {...props} socket={socket} />

                <HeaderTabs {...props} />

                <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                    ← Tornar a la pàgina principal
                </Pressable>

                <h1>Gestió de castellers</h1>

                <div className="page-layout">
                    <div className="navigation-tabs">
                        <button
                            className={`tab-button ${activeTab === 'usuaris' ? 'active' : ''}`}
                            onClick={() => changeTab('usuaris')}
                        >
                            <i className="fas fa-users"></i> Usuaris
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'crear' ? 'active' : ''}`}
                            onClick={() => changeTab('crear')}
                        >
                            <i className="fas fa-user-plus"></i> Crear
                        </button>
                        {hasFamiliesFeature() ? (
                            <button
                                className={`tab-button ${activeTab === 'families' ? 'active' : ''}`}
                                onClick={() => changeTab('families')}
                            >
                                <i className="fas fa-home"></i> Famílies
                            </button>
                        ) : null}
                        <button
                            className={`tab-button ${activeTab === 'exportar' ? 'active' : ''}`}
                            onClick={() => changeTab('exportar')}
                        >
                            <i className="fas fa-file-export"></i> Exportar
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'importar' ? 'active' : ''}`}
                            onClick={() => changeTab('importar')}
                        >
                            <i className="fas fa-file-import"></i> Importar
                        </button>
                        {canManageUsers ? (
                            <button
                                className={`tab-button ${activeTab === 'paperera' ? 'active' : ''}`}
                                onClick={() => changeTab('paperera')}
                            >
                                <i className="fas fa-trash-alt"></i> Paperera
                            </button>
                        ) : null}
                        <button
                            className={`tab-button ${activeTab === 'permisos' ? 'active' : ''}`}
                            onClick={() => changeTab('permisos')}
                        >
                            <i className="fas fa-shield-alt"></i> Permisos
                        </button>
                    </div>

                    <div className="page-content">
                        {renderTabContent()}
                    </div>
                </div>

                <div style={{ height: '50px' }}></div>
            </div>

            {/* History Panel */}
            <HistoryPanel 
                socket={socket} 
                history={casteHistory}
                onRefresh={() => socket.emit('.request_casteller_history', { limit: 50 })}
            />

            {/* Undo Toast Notification */}
            {undoToast ? (
                <div 
                    className={`history-panel-toast ${undoToast.type}`}
                    style={{
                        position: 'fixed',
                        bottom: 100,
                        right: 20,
                        background: undoToast.type === 'success' ? '#28a745' : '#dc3545',
                        color: 'white',
                        padding: '12px 20px',
                        borderRadius: 8,
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        zIndex: 1002
                    }}
                >
                    <i className={`fas ${undoToast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                    {undoToast.message}
                </div>
            ) : null}
        </>
    );
}

export default GestioApp;
